import { BILLING_LIVE } from '@lib/constants/billing'
import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Building2, Landmark, Mail, Plus, ToggleRight, Trash2, UserCheck, UserPlus, UserX, Users, Wallet } from 'lucide-react'
import { LoginToTenantButton } from '@/components/LoginToTenantButton'
import { TenantFeaturesSection } from '@/components/TenantFeaturesSection'
import { TenantBillingSection } from '@/components/TenantBillingSection'
import { Badge } from '@/components/ui/badge'
import {
  useTenant,
  useCreateTenant,
  useUpdateTenant,
  useTenantUsers,
  useInviteTenantAdmin,
  useDeactivateTenantUser,
  useDeleteInvitedTenantUser,
  useReactivateTenantUser,
  useResendTenantInvite,
} from '@/hooks/useTenants'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { ChoiceCard, ChoiceGroup } from '@/components/ui/choice-card'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { LoadingState, ErrorState } from '@/components/ui/states'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { FormActions } from '@/components/ui/form-actions'
import { SectionNav, type PageSection } from '@/components/ui/section-nav'
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableActionsHead,
  TableActionsCell,
  TableEmptyRow,
} from '@/components/ui/table'
import { RowActions, type RowAction } from '@/components/ui/row-actions'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { UserStatusBadge, inviteActionLabel } from '@/components/UserStatusBadge'
import { createTenantInputSchema, updateTenantInputSchema, type TenantPlan } from '@lib/input/tenant.input'
import { inviteByEmailInputSchema } from '@lib/input/user.input'
import type { TenantDto } from '@lib/dto/tenant.dto'
import type { UserDto } from '@lib/dto/user.dto'
import type { Role } from '@lib/enums/role.enum'

function roleLabel(role: Role): string {
  return role.replace(/_/g, ' ').toLowerCase()
}

/** Inline invite form for an organization admin on this tenant. */
function InviteTenantAdminForm({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const invite = useInviteTenantAdmin(tenantId)

  function save(): void {
    setError(null)
    const parsed = inviteByEmailInputSchema.safeParse({ name, email })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the fields and try again.')
      return
    }
    invite.mutate(parsed.data, { onSuccess: onDone, onError: (e) => setError(requestErrorMessage(e)) })
  }

  return (
    <Card className="space-y-4 p-4">
      <Field label="Name" htmlFor="tenant-invite-name">
        <Input
          id="tenant-invite-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Full name"
          autoFocus
        />
      </Field>
      <Field label="Email" htmlFor="tenant-invite-email">
        <Input
          id="tenant-invite-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
        />
      </Field>
      {error ? <FormError>{error}</FormError> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={save}
          disabled={name.trim() === '' || email.trim() === ''}
          pending={invite.isPending}
        >
          {invite.isPending ? 'Sending…' : 'Send invite'}
        </Button>
      </FormActions>
    </Card>
  )
}

function TenantUserRow({ tenantId, user, isBillingOwner }: { tenantId: string; user: UserDto; isBillingOwner: boolean }) {
  const deactivate = useDeactivateTenantUser(tenantId)
  const deleteInvited = useDeleteInvitedTenantUser(tenantId)
  const reactivate = useReactivateTenantUser(tenantId)
  const resend = useResendTenantInvite(tenantId)
  const [resent, setResent] = React.useState(false)


  /**
   * The action that ends an account. An invitation nobody accepted is deleted —
   * there is nothing to keep, and holding the address would stop the invite being
   * re-sent to a corrected one. An account in use is deactivated, reversibly.
   */
  /**
   * The one action that ends an account: an unaccepted invitation is deleted,
   * since there is nothing to keep and holding the address would stop it being
   * re-sent to a corrected one; an account in use is deactivated, reversibly.
   */
  function lifecycleAction(): RowAction {
    if (user.status === 'DEACTIVATED') {
      return {
        label: 'Reactivate',
        icon: UserCheck,
        onSelect: () => reactivate.mutate({ userId: user.id }),
        disabled: reactivate.isPending,
      }
    }
    if (user.status === 'PENDING') {
      return {
        label: 'Delete invitation',
        icon: Trash2,
        destructive: true,
        onSelect: () => deleteInvited.mutate({ userId: user.id }),
        disabled: deleteInvited.isPending,
      }
    }
    return {
      label: 'Deactivate',
      icon: UserX,
      destructive: true,
      onSelect: () => deactivate.mutate({ userId: user.id }),
      disabled: isBillingOwner || deactivate.isPending,
      disabledReason: isBillingOwner ? 'The billing owner can’t be deactivated until billing is moved to someone else' : undefined,
    }
  }

  const actions: RowAction[] = [
    ...(user.status === 'PENDING'
      ? [
          {
            label: resent ? 'Sent' : inviteActionLabel(user.invitedAt),
            icon: Mail,
            priority: true,
            onSelect: () => resend.mutate({ userId: user.id }, { onSuccess: () => setResent(true) }),
            disabled: resend.isPending || resent,
          } satisfies RowAction,
        ]
      : []),
    lifecycleAction(),
  ]


  return (
    <TableRow>
      <TableCell>
        <p className="flex items-center gap-2 text-sm font-medium">
          {user.name}
          {isBillingOwner ? <Badge tone="info">Billing owner</Badge> : null}
        </p>
        <p className="text-xs text-ink-muted">{user.email}</p>
      </TableCell>
      <TableCell className="text-sm text-ink-muted capitalize">{roleLabel(user.role)}</TableCell>
      <TableCell>
        <UserStatusBadge
          status={user.status}
          invitedAt={user.invitedAt}
          inviteExpiresAt={user.inviteExpiresAt}
        />
      </TableCell>
      <TableActionsCell>
        <RowActions label={user.name} actions={actions} />
      </TableActionsCell>
    </TableRow>
  )
}

/** Users section: invite organization admins + list the tenant's people. */
function UsersSection({ tenantId, billingOwnerId }: { tenantId: string; billingOwnerId: string | null }) {
  const usersQuery = useTenantUsers(tenantId)
  const [inviting, setInviting] = React.useState(false)

  function renderTable() {
    if (usersQuery.isPending) {
      return (
        <LoadingState label="Loading users…" />
      )
    }
    if (usersQuery.isError) {
      return (
        <ErrorState message={requestErrorMessage(usersQuery.error)} onRetry={() => void usersQuery.refetch()} />
      )
    }
    const users = usersQuery.data.users
    return (
      <Table
        // Every user of the tenant, not only its admins — the invite here makes
        // admins, but clients and venue admins are listed too.
        heading={`${String(users.length)} ${users.length === 1 ? 'person' : 'people'}`}
        actions={
          inviting ? undefined : (
            <Button type="button" variant="ghost" size="sm" onClick={() => setInviting(true)}>
              <UserPlus className="h-4 w-4" /> Invite organization admin
            </Button>
          )
        }
      >
        <TableHeader>
          <TableHead>Person</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Status</TableHead>
          <TableActionsHead />
        </TableHeader>
        <TableBody>
          {users.length === 0 ? (
            <TableEmptyRow colSpan={4}>
              No users yet. Invite an organization admin to staff this tenant.
            </TableEmptyRow>
          ) : null}
          {users.map((user) => (
            <TenantUserRow key={user.id} tenantId={tenantId} user={user} isBillingOwner={user.id === billingOwnerId} />
          ))}
        </TableBody>
      </Table>
    )
  }

  return (
    <div className="space-y-4">
      {inviting ? <InviteTenantAdminForm tenantId={tenantId} onDone={() => setInviting(false)} /> : null}
      {renderTable()}
    </div>
  )
}

/** Details section: rename the organization. Keyed by tenant id+name so a
 * successful save remounts with the new value without a setState-in-effect. */
function DetailsSection({ tenant }: { tenant: TenantDto }) {
  return <DetailsForm key={`${tenant.id}:${tenant.name}`} tenant={tenant} />
}

function DetailsForm({ tenant }: { tenant: TenantDto }) {
  const updateTenant = useUpdateTenant()
  const [name, setName] = React.useState(tenant.name)
  const [error, setError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError(null)
    setSaved(false)
    const parsed = updateTenantInputSchema.safeParse({ name })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Invalid input')
      return
    }
    try {
      await updateTenant.mutateAsync({ id: tenant.id, input: parsed.data })
      setSaved(true)
    } catch (e) {
      setError(requestErrorMessage(e))
    }
  }

  return (
    <form className="max-w-md space-y-4" onSubmit={handleSubmit} noValidate>
      <Field label="Organization name" htmlFor="tenant-name" error={error}>
        <Input
          id="tenant-name"
          value={name}
          onChange={(e) => {
            setName(e.target.value)
            setSaved(false)
          }}
          placeholder="Willow Creek Events"
        />
      </Field>
      <p className="text-sm text-ink-muted">
        {tenant.venueCount} venues · {tenant.userCount} users · {tenant.eventCount} events
      </p>
      <FormActions>
        <Button pending={updateTenant.isPending} type="submit" disabled={name.trim() === tenant.name}>
          {updateTenant.isPending ? 'Saving…' : saved ? 'Saved' : 'Save changes'}
        </Button>
      </FormActions>
    </form>
  )
}

/** Create-only form (new tenant has no sections yet). */
function CreateTenant() {
  const navigate = useNavigate()
  const createTenant = useCreateTenant()
  const [name, setName] = React.useState('')
  const [plan, setPlan] = React.useState<TenantPlan>('comped')
  const [error, setError] = React.useState<string | null>(null)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError(null)
    const parsed = createTenantInputSchema.safeParse({ name, plan })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Invalid input')
      return
    }
    try {
      const created = await createTenant.mutateAsync(parsed.data)
      navigate(`/tenants/${created.id}#users`)
    } catch (e) {
      setError(requestErrorMessage(e))
    }
  }

  return (
    <form className="space-y-6" onSubmit={handleSubmit} noValidate>
      <Field
        label="Organization name"
        htmlFor="tenant-name"
        error={error}
        hint="Its organization admins are invited next, from the Users tab."
      >
        <Input
          id="tenant-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Willow Creek Events"
          autoFocus
        />
      </Field>
      <ChoiceGroup legend="Billing" columns={2}>
        <ChoiceCard
          type="radio"
          name="plan"
          checked={plan === 'comped'}
          onSelect={() => setPlan('comped')}
          title="Comped"
          description="Never billed."
        />
        <ChoiceCard
          type="radio"
          name="plan"
          checked={plan === 'paid'}
          onSelect={() => setPlan('paid')}
          disabled={!BILLING_LIVE}
          title="Paid"
          description={
            BILLING_LIVE
              ? 'The first organization admin you invite accepts the terms, adds a venue and subscribes with the free trial, and becomes the billing owner. Removed after 30 days if they never finish.'
              : 'Available once billing is live. Until then there is no way for a new company to pay.'
          }
        />
      </ChoiceGroup>
      <FormActions>
        <Button type="button" variant="outline" onClick={() => navigate('/tenants')}>
          Cancel
        </Button>
        <Button pending={createTenant.isPending} type="submit">
          <Plus className="h-4 w-4" />
          {createTenant.isPending ? 'Adding…' : 'Add tenant'}
        </Button>
      </FormActions>
    </form>
  )
}

/**
 * Super-admin create/edit page for a tenant. Create is a single name form; edit
 * is a sectioned page (Details / Users) navigated by a left subnav.
 */
export function TenantFormPage() {
  const { tenantId } = useParams<{ tenantId: string }>()
  const isEdit = tenantId !== undefined
  const navigate = useNavigate()

  // Prefer the dedicated get when editing so we don't depend on the list cache.
  const tenantQuery = useTenant(tenantId ?? '')

  const existing: TenantDto | undefined = isEdit ? tenantQuery.data : undefined

  const backLink = (
    <button
      type="button"
      onClick={() => navigate('/tenants')}
      className="inline-flex items-center gap-1.5 text-sm text-ink-muted transition-colors hover:text-ink"
    >
      <ArrowLeft className="h-4 w-4" /> Tenants
    </button>
  )

  if (!isEdit) {
    return (
      <PageContainer className="space-y-6">
        <div className="space-y-3">
          {backLink}
          <PageHeader
            title="New tenant"
            description="Add an organization. You can invite its organization admins next."
          />
        </div>
        <CreateTenant />
      </PageContainer>
    )
  }

  if (tenantQuery.isPending) return <LoadingState label="Loading tenant…" />
  if (tenantQuery.isError) {
    return <ErrorState message={requestErrorMessage(tenantQuery.error)} onRetry={() => void tenantQuery.refetch()} />
  }
  if (!existing) {
    return <ErrorState message="That tenant no longer exists." onRetry={() => navigate('/tenants')} />
  }

  const sections: PageSection[] = [
    {
      id: 'details',
      label: 'Details',
      icon: Landmark,
      description: 'Rename this organization.',
      content: <DetailsSection tenant={existing} />,
    },
    {
      id: 'users',
      label: 'Users',
      icon: Users,
      description: 'Invite organization admins and manage people in this tenant.',
      content: <UsersSection tenantId={existing.id} billingOwnerId={existing.billingOwner?.id ?? null} />,
    },
    {
      id: 'billing',
      label: 'Billing',
      icon: Wallet,
      description: 'Who pays for this organization. Only an active organization admin can.',
      // Keyed by the owner so the picker starts again from whoever holds it now,
      // however it changed — a transfer, or another super admin's save.
      content: <TenantBillingSection key={existing.billingOwner?.id ?? 'none'} tenant={existing} />,
    },
    {
      id: 'features',
      label: 'Features',
      icon: ToggleRight,
      description:
        'The parts of the product this organization has. Switching one off hides it from them and refuses it on the API.',
      content: <TenantFeaturesSection tenant={existing} />,
    },
  ]

  return (
    <PageContainer width="wide" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-3">
          {backLink}
          <h1 className="text-2xl font-semibold tracking-tight">{existing.name}</h1>
          <p className="text-sm text-ink-muted">
            <Building2 className="mr-1 inline h-3.5 w-3.5" />
            {existing.venueCount} venues · {existing.userCount} users · {existing.eventCount} events
          </p>
          <p className="text-sm text-ink-muted">
            Log in to work inside this organization (venues, events, users) as a super admin.
          </p>
        </div>
        <LoginToTenantButton tenantId={existing.id} fullLabel />
      </div>
      <SectionNav sections={sections} ariaLabel="Tenant settings" />
    </PageContainer>
  )
}
